import React, { memo, useState, ChangeEvent } from 'react'
import { Autocomplete } from 'components/forms/autocomplete/DPLAutocomplete'
import { Button } from 'transfix-ui/components/Button'
import { ButtonGroup } from 'transfix-ui/components/ButtonGroup'
import { Box, InputLabel } from 'transfix-ui/core'
import { COUNT_LOADS_RANGE_OPTIONS } from 'apps/spot-quote-control-panel/constants/spotBiddingConfig'
import {
  RANGE_TYPE,
  ICountLoadsQuotedCriteria,
  ISpotBiddingRule,
  GEOGRAPHIC_CRITERIA_TYPE,
} from 'apps/spot-quote-control-panel/types/spotBiddingConfig'
import { TextInput } from 'components/atoms'
import { Checkbox } from 'transfix-ui'
import { initialRangeType } from '../../utils/initialRangeType'

interface IValidationError {
  type: string
  message: string
}

export interface ICountLoadsQuotedErrors {
  pickupType?: IValidationError
  deliveryType?: IValidationError
  pickupValue?: IValidationError
  deliveryValue?: IValidationError
  rangeStart?: IValidationError
  rangeEnd?: IValidationError
}

interface ICountLoadsQuotedValueProps {
  value?: RecursivePartial<ICountLoadsQuotedCriteria>
  onChange: (change: RecursivePartial<ISpotBiddingRule>) => void
  error?: string
  errors?: ICountLoadsQuotedErrors
}

export const CountLoadsQuotedValue = memo<ICountLoadsQuotedValueProps>(
  ({ error, errors, onChange, value: countLoadsQuotedValue }) => {
    const countLoadsQuotedCriteria = {
      pickupType: GEOGRAPHIC_CRITERIA_TYPE.datMarket,
      pickupValue: undefined,
      deliveryType: GEOGRAPHIC_CRITERIA_TYPE.datMarket,
      deliveryValue: undefined,
      rangeStart: undefined,
      rangeEnd: undefined,
      isSamePickupDelivery: false,
      isDifferentPickupDelivery: false,
      ...countLoadsQuotedValue,
    }
    const {
      deliveryType,
      deliveryValue,
      isDifferentPickupDelivery,
      isSamePickupDelivery,
      pickupType,
      pickupValue,
      rangeEnd = '',
      rangeStart = '',
    } = countLoadsQuotedCriteria
    const anyMarket = 'Any Market'
    const initialAnyPickupMarket = pickupValue?.toUpperCase() === anyMarket.toUpperCase()
    const initialAnyDeliveryMarket = deliveryValue?.toUpperCase() === anyMarket.toUpperCase()

    const [rangeType, setRangeType] = useState(initialRangeType(rangeStart, rangeEnd))
    const [anyPickupMarket, setAnyPickupMarket] = useState<boolean>(initialAnyPickupMarket)
    const [anyDeliveryMarket, setAnyDeliverymarket] = useState<boolean>(initialAnyDeliveryMarket)

    const rangeTypeHandler = (tempRangeType: string) => {
      if (tempRangeType === RANGE_TYPE.less) {
        onChange({ countLoadsQuotedCriteria: { ...countLoadsQuotedCriteria, rangeStart: '' } })
      } else if (tempRangeType === RANGE_TYPE.more) {
        onChange({ countLoadsQuotedCriteria: { ...countLoadsQuotedCriteria, rangeEnd: '' } })
      }
      setRangeType(tempRangeType)
    }

    const setRangeStart = ({ target: { value: count } }: ChangeEvent<HTMLInputElement>) => {
      onChange({ countLoadsQuotedCriteria: { ...countLoadsQuotedCriteria, rangeStart: count } })
    }

    const setRangeEnd = ({ target: { value: count } }: ChangeEvent<HTMLInputElement>) => {
      onChange({ countLoadsQuotedCriteria: { ...countLoadsQuotedCriteria, rangeEnd: count } })
    }

    const setPickupLocation = (location: string) => {
      onChange({
        countLoadsQuotedCriteria: {
          ...countLoadsQuotedCriteria,
          pickupValue: location,
        },
      })
    }

    const datMarketPickupHandler = ({
      fullSelection,
    }: {
      value: number
      fullSelection?: { name: string }
    }) => {
      setPickupLocation(fullSelection?.name || '')
    }

    const anyPickupMarketHandler = ({ target: { checked } }: ChangeEvent<HTMLInputElement>) => {
      if (checked) {
        onChange({
          countLoadsQuotedCriteria: { ...countLoadsQuotedCriteria, pickupValue: anyMarket },
        })
      }
      setAnyPickupMarket(checked)
    }

    const setDeliveryLocation = (location: string) => {
      onChange({
        countLoadsQuotedCriteria: {
          ...countLoadsQuotedCriteria,
          deliveryValue: location,
        },
      })
    }

    const datMarketDeliveryHandler = ({
      fullSelection,
    }: {
      value: number
      fullSelection?: { name: string }
    }) => {
      setDeliveryLocation(fullSelection?.name || '')
    }

    const anyDeliveryMarketHandler = ({ target: { checked } }: ChangeEvent<HTMLInputElement>) => {
      if (checked) {
        onChange({
          countLoadsQuotedCriteria: { ...countLoadsQuotedCriteria, deliveryValue: anyMarket },
        })
      }
      setAnyDeliverymarket(checked)
    }

    const placeValue = (criteriaType: GEOGRAPHIC_CRITERIA_TYPE, value?: string) => {
      if (!value) return undefined
      if (criteriaType === GEOGRAPHIC_CRITERIA_TYPE.cityState) {
        const [city, state] = value.split(', ')
        return { city, state }
      }
      if (criteriaType === GEOGRAPHIC_CRITERIA_TYPE.state) {
        return { state: value }
      }
      if (criteriaType === GEOGRAPHIC_CRITERIA_TYPE.datMarket) {
        return { name: value }
      }

      return undefined
    }

    const isSamePickupDeliveryChangeHandler = ({
      target: { checked },
    }: ChangeEvent<HTMLInputElement>) => {
      if (checked) {
        onChange({
          countLoadsQuotedCriteria: {
            ...countLoadsQuotedCriteria,
            isSamePickupDelivery: checked,
            isDifferentPickupDelivery: !checked,
          },
        })
      } else {
        onChange({
          countLoadsQuotedCriteria: {
            ...countLoadsQuotedCriteria,
            isSamePickupDelivery: checked,
            isDifferentPickupDelivery: checked,
          },
        })
      }
    }

    const isDifferentPickupDeliveryChangeHandler = ({
      target: { checked },
    }: ChangeEvent<HTMLInputElement>) => {
      if (checked) {
        onChange({
          countLoadsQuotedCriteria: {
            ...countLoadsQuotedCriteria,
            isSamePickupDelivery: !checked,
            isDifferentPickupDelivery: checked,
          },
        })
      } else {
        onChange({
          countLoadsQuotedCriteria: {
            ...countLoadsQuotedCriteria,
            isSamePickupDelivery: checked,
            isDifferentPickupDelivery: checked,
          },
        })
      }
    }

    return (
      <>
        <div className='row'>
          {pickupType === GEOGRAPHIC_CRITERIA_TYPE.datMarket && (
            <div className='col-md-6 col-xs-4'>
              <Autocomplete
                id='modal-count-loads-market-pickup'
                label='Pickup Dat Market'
                value={placeValue(pickupType, pickupValue)?.name}
                type='dat_market'
                onChange={datMarketPickupHandler}
                large
                validationMessage={
                  error || errors?.pickupValue?.message || errors?.pickupType?.message
                }
                disabled={anyPickupMarket}
                dataTest='modal-count-loads-market-pickup'
              />
              <div className='mt1'>
                <Checkbox
                  id='modal-count-loads-market-pickup-checkbox'
                  label='Any Market'
                  checked={anyPickupMarket}
                  onChange={anyPickupMarketHandler}
                  dataTest='modal-count-loads-market-pickup-checkbox'
                />
              </div>
            </div>
          )}
          {deliveryType === GEOGRAPHIC_CRITERIA_TYPE.datMarket && (
            <div className='col-md-6 col-xs-4'>
              <Autocomplete
                id='modal-count-loads-market-delivery'
                label='Delivery Dat Market'
                value={placeValue(deliveryType, deliveryValue)?.name}
                type='dat_market'
                onChange={datMarketDeliveryHandler}
                large
                validationMessage={
                  error || errors?.deliveryValue?.message || errors?.deliveryType?.message
                }
                disabled={anyDeliveryMarket}
                dataTest='modal-count-loads-market-delivery'
              />
              <div className='mt1'>
                <Checkbox
                  id='modal-count-loads-market-delivery-checkbox'
                  label='Any Market'
                  checked={anyDeliveryMarket}
                  onChange={anyDeliveryMarketHandler}
                  dataTest='modal-count-loads-market-delivery-checkbox'
                />
              </div>
            </div>
          )}
        </div>
        <div className='row'>
          <div className='col-md-6 col-xs-4'>
            <ButtonGroup
              value={rangeType}
              onChange={rangeTypeHandler}
              dataTest='modal-count-loads-button'>
              {COUNT_LOADS_RANGE_OPTIONS.map(option => (
                <Button key={option.label} value={option.value} size='large' fullWidth>
                  {option.label}
                </Button>
              ))}
            </ButtonGroup>
          </div>
          <div className='col-md-6 col-xs-4'>
            <div className='row'>
              {(rangeType === RANGE_TYPE.more || rangeType === RANGE_TYPE.between) && (
                <div className='col-md-6'>
                  <TextInput
                    label={rangeType === RANGE_TYPE.more ? 'More Than' : 'Starting'}
                    placeholder='Enter count'
                    id='modal-count-loads-more-than'
                    onChange={setRangeStart}
                    value={rangeStart || ''}
                    large
                    validationMessage={error || errors?.rangeStart?.message}
                    invalid={!!error || !!errors?.rangeStart}
                    dataTest='modal-count-loads-more-than'
                  />
                </div>
              )}
              {(rangeType === RANGE_TYPE.less || rangeType === RANGE_TYPE.between) && (
                <div className='col-md-6'>
                  <TextInput
                    label={rangeType === RANGE_TYPE.less ? 'Less Than' : 'Ending'}
                    placeholder='Enter count'
                    id='modal-count-loads-less-than'
                    onChange={setRangeEnd}
                    value={rangeEnd || ''}
                    large
                    validationMessage={
                      error || errors?.rangeEnd?.message || errors?.rangeStart?.message
                    }
                    invalid={!!error || !!errors?.rangeEnd || !!errors?.rangeStart}
                    dataTest='modal-count-loads-less-than'
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className='row'>
          <div className='w-100 col-md-6 col-xs-6'>
            <Box mb={1}>
              <InputLabel>Pickup/Delivery</InputLabel>
              <Checkbox
                label='Is Same Pickup Delivery?'
                checked={isSamePickupDelivery}
                onChange={isSamePickupDeliveryChangeHandler}
                dataTest='modal-count-loads-same-pickup-checkbox'
                disabled={isDifferentPickupDelivery}
              />
              <Checkbox
                label='Is Different Pickup Delivery?'
                checked={isDifferentPickupDelivery}
                onChange={isDifferentPickupDeliveryChangeHandler}
                dataTest='modal-count-loads-different-pickup-checkbox'
                disabled={isSamePickupDelivery}
              />
            </Box>
          </div>
        </div>
      </>
    )
  }
)
